// src/components/AdminPanel/ReservationApplications.js
import React, { useState, useEffect, useMemo } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import ReservationTable from "./ReservationTable";

const normalise = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "selected") return "accepted";
  if (s === "pending" || s === "") return "pending";
  return s;
};

const ReservationApplications = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [applications, setApplications] = useState([]);

  // Single realtime listener — all tabs share this data (zero duplicate reads)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "opportunityApplications"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setApplications(data);
      }
    );
    return () => unsubscribe();
  }, []);

  const counts = useMemo(() => {
    const result = { pending: 0, accepted: 0, rejected: 0, blocked: 0 };
    applications.forEach((app) => {
      const n = normalise(app.status);
      if (result[n] !== undefined) result[n]++;
    });
    return result;
  }, [applications]);

  const totalApplications = applications.length;
  const pendingCount = counts.pending;
  const acceptedCount = counts.accepted;
  const rejectedCount = counts.rejected;
  const blockedCount = counts.blocked;

  // ── Original theme ────────────────────────────────────────────────────
  const cardGradient = "linear-gradient(135deg, #FF8C00 0%, #E67E00 100%)";
  const highlightedGradient = "linear-gradient(135deg, #FF9F2E 0%, #FF8C00 100%)";

  const tabs = [
    { key: "pending", label: "Pending" },
    { key: "accepted", label: "Accepted" },
    { key: "rejected", label: "Rejected" },
    { key: "blocked", label: "Blocked" },
  ];

  return (
    <div className="dashboard-page">
      <h1 className="page-heading">
        <i className="fas fa-users" style={{ color: "#FF8C00" }}></i>
        Reservation Applications
      </h1>

      {/* ── Stats Cards — original 3-card orange theme ─────────────── */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "30px",
          flexWrap: "wrap",
        }}
      >
        {/* Total Applications Card — Highlighted */}
        <div
          style={{
            background: highlightedGradient,
            padding: "24px",
            borderRadius: "16px",
            color: "white",
            minWidth: "200px",
            boxShadow: "0 8px 20px rgba(255, 140, 0, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            transform: "translateY(-2px)",
            transition: "all 0.3s ease",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <i
              className="fas fa-calendar-check"
              style={{ fontSize: "28px", opacity: 0.9 }}
            ></i>
            <h3
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "500",
                opacity: 0.9,
              }}
            >
              Total Reservations
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {totalApplications}
          </p>
        </div>

        {/* Upcoming / Pending Card */}
        <div
          style={{
            background: cardGradient,
            padding: "24px",
            borderRadius: "16px",
            color: "white",
            minWidth: "200px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            transition: "all 0.3s ease",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <i
              className="fas fa-clock"
              style={{ fontSize: "28px", opacity: 0.9 }}
            ></i>
            <h3
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "500",
                opacity: 0.9,
              }}
            >
              Upcoming
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {pendingCount}
          </p>
        </div>

        {/* Accepted Card */}
        <div
          style={{
            background: cardGradient,
            padding: "24px",
            borderRadius: "16px",
            color: "white",
            minWidth: "160px",
            flex: 1,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            transition: "all 0.3s ease",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <i
              className="fas fa-check-circle"
              style={{ fontSize: "28px", opacity: 0.9 }}
            ></i>
            <h3
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "500",
                opacity: 0.9,
              }}
            >
              Accepted
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {acceptedCount}
          </p>
        </div>

        {/* Rejected Card */}
        <div
          style={{
            background: cardGradient,
            padding: "24px",
            borderRadius: "16px",
            color: "white",
            minWidth: "160px",
            flex: 1,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            transition: "all 0.3s ease",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <i
              className="fas fa-times-circle"
              style={{ fontSize: "28px", opacity: 0.9 }}
            ></i>
            <h3
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "500",
                opacity: 0.9,
              }}
            >
              Rejected
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {rejectedCount}
          </p>
        </div>

        {/* Blocked Card */}
        <div
          style={{
            background: cardGradient,
            padding: "24px",
            borderRadius: "16px",
            color: "white",
            minWidth: "160px",
            flex: 1,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            transition: "all 0.3s ease",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <i
              className="fas fa-ban"
              style={{ fontSize: "28px", opacity: 0.9 }}
            ></i>
            <h3
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "500",
                opacity: 0.9,
              }}
            >
              Blocked
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>
            {blockedCount}
          </p>
        </div>
      </div>

      {/* ── Tabs — original styling, now 4 tabs ────────────────────── */}
      <div
        className="category-tabs"
        style={{
          display: "flex",
          gap: "0",
          borderBottom: "1px solid #E2E8F0",
          marginBottom: "20px",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? "tab active" : "tab"}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "12px 24px",
              background: activeTab === tab.key ? "#FF8C00" : "transparent",
              color: activeTab === tab.key ? "white" : "#4A5568",
              border: "none",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.2s",
              marginRight: "4px",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Reservation Table ───────────────────────────────────────── */}
      <ReservationTable activeTab={activeTab} allApplications={applications} />
    </div>
  );
};

export default ReservationApplications;
